/*
 |-----------------------------------------------------------------------------
 | Los temas
 |-----------------------------------------------------------------------------
 | Un tema NO es una paleta: es un conjunto de luminosidades y una regla sobre
 | cuánto matiz de la marca heredan los grises. El color lo pone el estudio.
 |
 | Por eso son cuatro y no cuarenta. Lo que diferencia una galería de otra es la
 | marca de quien la publica; nuestro trabajo es que cualquier marca funcione en
 | cualquiera de estos cuatro escenarios, no ofrecer un catálogo.
 */

export type ThemeName = 'galeria' | 'editorial' | 'calido' | 'minimo' | 'marketing'

/** Lo que puede pedir un tenant. `auto` sigue la preferencia del visitante. */
/**
 * Los temas que un ESTUDIO puede elegir.
 *
 * `marketing` no es uno: es el de nuestra propia web, y la API lo rechaza
 * —`in:auto,galeria,editorial,calido,minimo`—. Tenerlo dentro de `ThemeChoice`
 * hacía que el tipo admitiera un valor que el servidor devuelve como 422.
 */
export type TenantTheme = Exclude<ThemeName, 'marketing'>

export type ThemeChoice = TenantTheme | 'auto'

export interface ThemeDefinition {
  label: string
  description: string
  mode: 'light' | 'dark'

  /**
   * Rampa neutra en L de OKLCH, de la superficie al texto.
   *
   * Están escritas a mano y no generadas por una curva porque los saltos que
   * importan no son regulares: entre `surface` y `raised` hace falta lo justo
   * para que una tarjeta se despegue sin dibujar una caja, y entre `border` y
   * `borderStrong` bastante más, porque uno es una línea de separación y el
   * otro delimita un campo que hay que poder ver dónde empieza.
   */
  neutrals: {
    surface: number
    raised: number
    sunken: number
    border: number
    borderStrong: number
    textSubtle: number
    textMuted: number
    text: number
  }

  /**
   * Croma de los neutros: cuánto matiz de la marca heredan los grises.
   *
   * Es el ajuste que más cambia la sensación por menos código. Con 0 el gris es
   * neutro puro; con 0,02 un estudio de identidad cálida ve grises cálidos y el
   * conjunto deja de parecer «su logo pegado sobre nuestra app». Por encima de
   * ~0,03 los grises se leen como color y compiten con las fotos.
   */
  neutralChroma: number

  /**
   * L de la pista de un medidor: barra de progreso, cuota, subida.
   *
   * Tiene token propio y no reutiliza `sunken` porque no es una superficie: es
   * la AUSENCIA de medida. Con el matiz de la marca y la cuota a cero, la pista
   * se lee como una barra llena de un color claro en vez de como una vacía —
   * exactamente lo contrario de lo que dice. Va casi sin croma y un paso más
   * lejos de la superficie, para que se lea como surco y el relleno, que sí
   * lleva la marca, no tenga con quién competir.
   */
  trackL: number

  /** Rango de L dentro del que se busca un acento que cumpla contraste. */
  accentRange: readonly [number, number]

  /** L del fondo teñido para insignias, estados activos y hover suaves. */
  accentSubtleL: number

  /** L de los colores de estado. Suben en oscuro para no apagarse. */
  statusL: number
}

export const THEMES: Record<ThemeName, ThemeDefinition> = {
  /*
   * El tema del portal. Es el que verá el cliente final salvo que su estudio
   * elija otra cosa, y esa decisión no es estética: sobre fondo oscuro una foto
   * se juzga mejor porque el ojo no está compensando el brillo de alrededor.
   * Juzgar fotos es literalmente lo que el cliente vino a hacer.
   */
  galeria: {
    label: 'Galería oscura',
    description: 'Fondo oscuro, la foto manda. Lo que usan los estudios de proofing.',
    mode: 'dark',
    neutrals: {
      surface: 0.17,
      raised: 0.22,
      sunken: 0.13,
      border: 0.3,
      borderStrong: 0.54,
      textSubtle: 0.61,
      textMuted: 0.74,
      text: 0.96,
    },
    trackL: 0.13,
    neutralChroma: 0.012,
    accentRange: [0.55, 0.82],
    accentSubtleL: 0.28,
    statusL: 0.72,
  },

  /*
   * Claro y aireado, para estudios de identidad limpia. Es el tema del panel y
   * de la web pública cuando el visitante no ha pedido oscuro.
   */
  editorial: {
    label: 'Editorial claro',
    description: 'Blanco, mucho aire y tipografía marcada. Para marcas limpias.',
    mode: 'light',
    neutrals: {
      surface: 1,
      raised: 0.985,
      sunken: 0.965,
      border: 0.9,
      borderStrong: 0.63,
      textSubtle: 0.54,
      textMuted: 0.45,
      text: 0.24,
    },
    trackL: 0.96,
    neutralChroma: 0.006,
    accentRange: [0.42, 0.68],
    accentSubtleL: 0.955,
    statusL: 0.52,
  },

  /*
   * El mismo esqueleto que `editorial` con el croma de los neutros al triple y
   * la superficie ligeramente por debajo del blanco. Suena a poco y es el que
   * más cambia el carácter: los grises dejan de ser grises y pasan a ser papel
   * del color de la marca.
   */
  calido: {
    label: 'Cálido',
    description: 'Los grises heredan el tono de tu marca. Sensación de papel.',
    mode: 'light',
    neutrals: {
      surface: 0.985,
      raised: 1,
      sunken: 0.955,
      border: 0.89,
      borderStrong: 0.62,
      textSubtle: 0.53,
      textMuted: 0.44,
      text: 0.22,
    },
    trackL: 0.95,
    neutralChroma: 0.019,
    accentRange: [0.42, 0.66],
    accentSubtleL: 0.94,
    statusL: 0.52,
  },

  /*
   * Neutros sin nada de matiz. Existe porque hay marcas cuyo color no admite
   * compañía —un magenta saturado tiñendo los grises marea— y porque algunos
   * estudios quieren que la única nota de color sea la fotografía.
   */
  minimo: {
    label: 'Mínimo',
    description: 'Grises puros, sin matiz. El color sólo aparece en las acciones.',
    mode: 'light',
    neutrals: {
      surface: 1,
      raised: 1,
      sunken: 0.97,
      border: 0.91,
      borderStrong: 0.63,
      textSubtle: 0.53,
      textMuted: 0.44,
      text: 0.2,
    },
    trackL: 0.96,
    neutralChroma: 0,
    accentRange: [0.4, 0.62],
    accentSubtleL: 0.96,
    statusL: 0.5,
  },

  /*
   |---------------------------------------------------------------------------
   | Tinta y papel — NUESTRA identidad, no una opción para el estudio
   |---------------------------------------------------------------------------
   | Está fuera de `TENANT_THEMES` a propósito: no aparece en los ajustes ni el
   | backend lo acepta como valor de `branding.theme`. Es el tema de la web
   | pública, el único sitio del producto donde el color es nuestro y no del
   | cliente.
   |
   | Y es casi sin color a propósito. La propuesta es «tus entregas, con tu
   | nombre; nosotros desaparecemos», así que nuestra identidad no debería
   | competir con la de los estudios: debería ser el terreno neutro donde su
   | color destaca. En la práctica, la única nota saturada de toda la web es el
   | demo de temas, que es justo donde aparecen los colores de los clientes. La
   | página defiende la tesis con su propia forma.
   |
   | El azul anterior era `blue-600` de Tailwind: no «un azul», sino EL color
   | por defecto del ecosistema, el de cada tutorial y cada plantilla. Con
   | tipografía del sistema y todo centrado, la web era la plantilla genérica de
   | SaaS. Vendiendo a fotógrafos —gente con criterio visual entrenado, que
   | juzga una herramienta de entrega por cómo entrega ella— eso no es un
   | problema de gusto sino de credibilidad.
   */
  marketing: {
    label: 'Tinta y papel',
    description: 'La web pública. Sin color propio, para que destaque el del estudio.',
    mode: 'light',
    neutrals: {
      // El papel está por debajo del blanco y las tarjetas POR ENCIMA: se
      // levantan del fondo en vez de hundirse, como una hoja sobre la mesa.
      surface: 0.975,
      raised: 1,
      sunken: 0.945,
      border: 0.88,
      borderStrong: 0.6,
      textSubtle: 0.5,
      textMuted: 0.4,
      // Tinta, no gris oscuro. Es el contraste que sostiene la página entera
      // cuando no hay color que la sostenga.
      text: 0.18,
    },
    neutralChroma: 0.011,
    // Rango de TINTA. Los demás temas empiezan en 0,40 porque un acento muy
    // oscuro sobre fondo claro se lee como texto y no como acción; aquí es
    // exactamente lo que se busca: el botón es un bloque de tinta.
    accentRange: [0.2, 0.32],
    accentSubtleL: 0.93,
    trackL: 0.94,
    statusL: 0.5,
  },
}

export const DEFAULT_BRAND_COLOR = '#2563eb'

/** Matices fijos de los estados. No dependen de la marca: verde es verde. */
export const STATUS_HUES = {
  positive: 152,
  warning: 78,
  danger: 27,
} as const

/**
 * Los que un estudio puede elegir.
 *
 * Escrito a mano y no derivado de `THEMES` porque `marketing` no es una opción
 * suya: es nuestra identidad. Derivarlo con `Object.keys` haría que apareciera
 * en los ajustes en cuanto alguien añadiera un tema interno, que es justo la
 * clase de fuga que no se nota hasta que un cliente la ve.
 *
 * El backend valida contra esta misma lista (`TenantController::update`).
 */
export const THEME_NAMES: readonly TenantTheme[] = ['galeria', 'editorial', 'calido', 'minimo']

/**
 * `Object.hasOwn` y no `value in THEMES`.
 *
 * El operador `in` consulta también la cadena de prototipos, así que
 * `isThemeName('constructor')` devolvía **true** —igual que `'__proto__'` y
 * `'toString'`— y `THEMES['constructor']` es una función. `deriveTheme`
 * reventaba después al leer `definition.neutrals.surface`: pantalla en blanco.
 *
 * Por la API está tapado, porque el backend valida contra una lista cerrada.
 * Pero `recallBranding()` lee de `localStorage`, que el usuario puede editar, y
 * sobre todo: esto deja de ser inofensivo en cuanto alguien enchufe otro origen
 * de datos y dé por hecho que la comprobación comprueba lo que parece.
 */
export function isThemeName(value: unknown): value is ThemeName {
  return typeof value === 'string' && Object.hasOwn(THEMES, value)
}

/** ¿Puede un estudio elegir este tema? `marketing` no. */
export function isTenantTheme(value: unknown): value is TenantTheme {
  // El `as` es sobre un valor que `isThemeName` ya validó; lo que falta es
  // decirle a TypeScript que `includes` acepta el conjunto más ancho.
  return isThemeName(value) && (THEME_NAMES as readonly ThemeName[]).includes(value)
}
